<template>
  <UIPage scrollable>
    <!-- check permissions for the "Create" button -->
    <SubHeader
      :show-btn="isEditAllowed"
      :handle-click="createMatrix"
      :button-text="$t('actions.create_new')"
    />
    <Loader v-if="isLoading" />

    <MatrixList
      v-else
      :list="matrixList"
      @edit="editMatrix"
      @view="viewMatrix"
      @clone="cloneMatrix"
      @delete="deleteMatrix"
    />

    <ModalConfirm ref="confirm-popup" />

    <modalContent ref="clone-matrix-modal" :max-width="370" height="75%" name="cloneMatrix" :title="$t('wam.clone.clone_matrix')" @close="closeMatrixModal" :showFooter="false">  
      <template #content>
        <div class="flex-1">
          <div class="flex flex-col flex-wrap max-w-xs">
            <FormulateForm #default="{ hasErrors }">
              <FormulateInput
                type="text"
                v-model="selectedMatrix.name" 
                :placeholder="$t('wam.clone.name')"
                :label="$t('wam.clone.name')"
                :validation="[
                  ['bail'], 
                  ['required'], 
                  ['matches', alphanumericSpacesHyphensUnderscoreRegex ]
                ]"
                :validation-messages="{
                  matches: $t('general.allowed_chars_only_hyp_undscr_error')
                }"
                error-behavior="value"
                class="formulate-lg"
              />
              
              <FormulateInput
                type="text"
                v-model="selectedMatrix.description"
                :placeholder="$t('wam.clone.description')"
                :label="$t('wam.clone.description')"
                class="formulate-lg"
              />

              <div class="flex justify-end mt-6">
                <button
                  role="button"
                  class="px-4 py-2 h-10 w-24 font-medium rounded-md ml-auto text-center"
                  :class="!hasErrors ? 'bg-blue-700 text-white cursor-pointer' : 'bg-gray-300 text-gray-500 cursor-not-allowed pointer-events-none'"
                  @click="handleCloneModal()"
                  :disabled="hasErrors"
                >
                  {{ $t('wam.clone.clone') }}
                </button>
              </div>
            </FormulateForm>
          </div>
        </div>
      </template>
    </modalContent>

    <modalContent
      ref="delete-matrix-modal-error"
      :max-width="600"
      name="deleteMatrix"
      :title="$t('wam.errors.delete_matrix')"
      submitButtonText="Ok"
      @hidden="resetDeleteMatrixError"
      @submit="closeDeleteMatrixErrorModal"
    >
      <template #content>
        <div class="error-modal">
          <div class="w-full error-modal__message">{{ errors.deleteMatrix }}</div>
      </div>
      </template>
    </modalContent>
  </UIPage>
</template>

<script>
import { mapMutations } from "vuex";

import SubHeader from "@/components/SubHeader";
import Loader from "@shared/components/WrappedLoader";
import ModalConfirm from "@shared/modal-confirm";
import modalContent from "@/components/modal-content";
import handleError from "@shared/mixins/handleError";

import { MatrixList } from "../components";
import { checkPermission } from "@shared/utils/functions";
import { VIEW_MODE_MAP } from "../utils/constants";
import api from "../api";
import { alphanumericSpacesHyphensUnderscoreRegex } from "@shared/utils/validation/regex.js";

export default {
  name: "work-allocation-matrix-list",
  mixins: [handleError],

  components: {
    SubHeader,
    Loader,
    MatrixList,
    ModalConfirm,
    modalContent,
  },

  props: {},

  data() {
    return {
      alphanumericSpacesHyphensUnderscoreRegex,
      isLoading: false,
      matrixList: [],
      selectedMatrix: {
        name: "",
        description: "",
      },
      cloningMatrix: null,
      errors: {
        deleteMatrix: null,
      }
    }
  },

  async mounted() {
    await this.getWorkAllocationList();
  },

  computed: {
    isEditAllowed() {
      return checkPermission("wam.edit");
    },
  },

  methods: {
    ...mapMutations({
      setSelectedWAM: "WAMStore/SET_CURRENT_WAM",
    }),

    createMatrix() {
      this.$router.push({
        name: "Work Allocation Matrix View",
        params: { action: VIEW_MODE_MAP.CREATE },
      });
    },
    editMatrix(matrix) {
      if (!this.isEditAllowed) return;
      this.setSelectedWAM(matrix);
      this.$router.push({
        name: "Work Allocation Matrix View",
        params: { id: matrix.id, action: VIEW_MODE_MAP.EDIT },
      });
    },
    viewMatrix(matrix) {
      this.setSelectedWAM(matrix);
      this.$router.push({
        name: "Work Allocation Matrix View",
        params: { id: matrix.id, action: VIEW_MODE_MAP.VIEW },
      });
    },
    // TODO
    async cloneMatrix(matrix) {
      if (!this.isEditAllowed) return;
        this.$refs["clone-matrix-modal"].showModal();
        this.cleanCloneInput()
        this.cloningMatrix = matrix;
        console.log("Clone work allocation matrix:", matrix); // TODO remove
    },
    async handleCloneModal(){
      try {
        let payload = this.selectedMatrix;
        await api.cloneWorkAllocationMatrixItem(this.cloningMatrix.id, payload)
        this.$toast.success(this.$t("wam.clone.cloned_message"));
        this.closeMatrixModal();
        this.getWorkAllocationList();
      }catch (error) {
        this.handleError(error);
      } 
    },
    cleanCloneInput(){
        this.selectedMatrix.name = "";
        this.selectedMatrix.description = "";
        this.cloningMatrix= null
    },

    closeMatrixModal() {
      this.cleanCloneInput();
      this.$refs["clone-matrix-modal"].hideModal();
    },

    async deleteMatrix(matrix) {
      if (!this.isEditAllowed) return;

      try {
        const isConfirmed = await this.$refs["confirm-popup"].show({
          message: this.$t('components.are_you_sure_you_want_to_delete'),
        });

        if (isConfirmed) {
          await api.deleteWorkAllocationMatrixItem(matrix.id);
          this.matrixList = this.matrixList.filter(item => item.id !== matrix.id);
          this.$toast.success(this.$t("general.delete_success"));
        }
      } catch (error) {
        this.errors.deleteMatrix = this.getErrorMessage(error, {
          fallback: this.$t("wam.delete_linked_wam_fail"),
        })
        this.$refs["delete-matrix-modal-error"].showModal();
        return;
      }
    },

    async getWorkAllocationList() {
      this.isLoading = true;
      try {
        const { data } = await api.getWorkAllocationMatrixList();
        this.matrixList = data;
      } catch (error) {
        this.handleError(error);
      } finally {
        this.isLoading = false;
      }
    },

    closeDeleteMatrixErrorModal() {
      this.$refs["delete-matrix-modal-error"].hideModal();
      this.resetDeleteMatrixError();
    },

    resetDeleteMatrixError() {
      this.errors.deleteMatrix = null;
    },
  },
};
</script>
<style lang="scss">
.formulate-lg.formulate-input input {
  font-family: 'Poppins', sans-serif;
  height: 3rem;
  max-height: 3rem;
  font-size: 0.875rem!important;
  padding-left: 1rem;
  padding-right: 1rem;
  color: #282828
}
.formulate-lg.formulate-input input:focus {
    border-color: var(--theme-color-main);
}
</style>

<style lang="scss" scoped>
.error-modal {
  max-height: 50vh;

  &__message {
    word-break: break-word;
    white-space: pre-wrap;
  }
}
</style>
